/* eslint-disable react/no-unescaped-entities */
/**
 * ExpertNetwork — Réseau de spécialistes CONFILEX (P1, MVP).
 *
 * Page réservée aux spécialistes à abonnement actif :
 *  - Onglet « Réseau » : réseau de spécialistes entre spécialistes avec filtres
 *    (profession, ville, territoire FR/CH, vérifiés, recherche libre)
 *  - Onglet « Conversations » : liste + chat interne (free / no payment)
 *
 * Mention déontologique obligatoire affichée en haut, et bandeau de gating
 * 403 si l'abonnement n'est pas actif.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, MapPin, MessageCircle, Send, Ban, Flag, ArrowLeft, BadgeCheck, X, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiClient } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function ExpertNetwork() {
  const [tab, setTab] = useState("directory");
  const [forbidden, setForbidden] = useState(null);

  return (
    <div className="min-h-screen bg-ivory">
      <Seo
        title="Réseau de spécialistes CONFILEX — Mise en relation gratuite entre professionnels abonnés"
        description="Réseau interne réservé aux spécialistes CONFILEX à abonnement actif. Contact direct, messagerie sécurisée, sans commission."
        canonical="/expert/network"
        noindex
      />

      <header className="border-b border-night/10 bg-paper">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <Link to="/expert/dashboard" className="text-xs text-night/55 hover:text-night inline-flex items-center gap-1.5 mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à mon espace spécialiste
          </Link>
          <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-2">
            CONFILEX · RÉSEAU D'EXPERTS
          </p>
          <h1 className="font-serif italic text-3xl md:text-4xl text-night leading-tight mb-3">
            Le réseau professionnel des spécialistes référencés CONFILEX.
          </h1>
          <p className="text-sm text-night/70 max-w-3xl leading-relaxed">
            Réseau interne réservé aux spécialistes à abonnement actif. Identifiez des
            confrères ou des professionnels complémentaires, échangez gratuitement
            dans un espace sécurisé.
          </p>
          <p className="text-[11px] text-night/55 italic mt-3 max-w-3xl">
            CONFILEX facilite la mise en relation entre professionnels référencés.
            Chaque spécialiste demeure libre, indépendant et responsable de ses échanges
            professionnels.
          </p>
        </div>
      </header>

      {forbidden ? (
        <ForbiddenBanner message={forbidden} />
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="max-w-6xl mx-auto px-6 py-8">
          <TabsList className="mb-6">
            <TabsTrigger value="directory" data-testid="tab-directory">Réseau</TabsTrigger>
            <TabsTrigger value="conversations" data-testid="tab-conversations">Mes conversations</TabsTrigger>
          </TabsList>
          <TabsContent value="directory">
            <Directory onForbidden={setForbidden} />
          </TabsContent>
          <TabsContent value="conversations">
            <Conversations onForbidden={setForbidden} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ForbiddenBanner({ message }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Card className="p-8 rounded-3xl border-amber-200 bg-amber-50 text-center" data-testid="subscription-required-banner">
        <ShieldCheck className="w-10 h-10 text-amber-600 mx-auto mb-4" />
        <h2 className="font-serif text-2xl text-night mb-3">Accès restreint</h2>
        <p className="text-sm text-night/75 leading-relaxed">{message}</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Button asChild className="rounded-full bg-night text-paper hover:bg-night/90">
            <Link to="/pricing">Voir les offres de référencement</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/expert/dashboard">Retour à mon espace</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// Réseau de spécialistes
// =============================================================================
function Directory({ onForbidden }) {
  const [loading, setLoading] = useState(true);
  const [experts, setExperts] = useState([]);
  const [filters, setFilters] = useState({ q: "", profession: "", city: "", territory: "", verified_only: false });
  const [selected, setSelected] = useState(null);

  const fetchExperts = useMemo(() => async (f) => {
    setLoading(true);
    try {
      const params = {};
      if (f.q) params.q = f.q;
      if (f.profession) params.profession = f.profession;
      if (f.city) params.city = f.city;
      if (f.territory) params.territory = f.territory;
      if (f.verified_only) params.verified_only = true;
      const { data } = await apiClient.get("/network/experts", { params });
      setExperts(data.experts || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        onForbidden(e.response.data?.detail || "Accès réservé aux spécialistes abonnés.");
      } else {
        toast.error("Impossible de charger le réseau pour le moment.");
      }
    } finally {
      setLoading(false);
    }
  }, [onForbidden]);

  useEffect(() => { fetchExperts(filters); /* eslint-disable-next-line */ }, []);

  const onSubmit = (e) => { e.preventDefault(); fetchExperts(filters); };

  return (
    <>
      <Card className="p-5 rounded-2xl border-slate-100 mb-6">
        <form onSubmit={onSubmit} className="grid sm:grid-cols-5 gap-3 items-end" data-testid="directory-filters">
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-night/60 mb-1.5 block">Recherche</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-night/40" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Nom, spécialité, profession…"
                className="pl-9"
                data-testid="filter-q"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-night/60 mb-1.5 block">Profession</label>
            <select
              value={filters.profession}
              onChange={(e) => setFilters({ ...filters, profession: e.target.value })}
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"
              data-testid="filter-profession"
            >
              <option value="">Toutes</option>
              <option value="avocat">Avocat</option>
              <option value="notaire">Notaire</option>
              <option value="expert-comptable">Expert-comptable</option>
              <option value="commissaire-justice">Commissaire de justice</option>
              <option value="administrateur-judiciaire">Administrateur judiciaire</option>
              <option value="expert-immobilier">Expert immobilier</option>
              <option value="cgp">CGP</option>
              <option value="fiscaliste">Fiscaliste</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-night/60 mb-1.5 block">Ville</label>
            <Input
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              placeholder="Paris…"
              data-testid="filter-city"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-night/60 mb-1.5 block">Territoire</label>
            <select
              value={filters.territory}
              onChange={(e) => setFilters({ ...filters, territory: e.target.value })}
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"
              data-testid="filter-territory"
            >
              <option value="">Tous</option>
              <option value="FR">France</option>
              <option value="CH">Suisse</option>
            </select>
          </div>
          <div className="sm:col-span-5 flex items-center justify-between gap-4 flex-wrap">
            <label className="text-sm text-night/70 flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.verified_only}
                onChange={(e) => setFilters({ ...filters, verified_only: e.target.checked })}
                data-testid="filter-verified"
              />
              Spécialistes vérifiés uniquement
            </label>
            <Button type="submit" className="rounded-full bg-night text-paper hover:bg-night/90" data-testid="filter-submit">
              Rechercher
            </Button>
          </div>
        </form>
      </Card>

      {loading ? (
        <div className="py-16 text-center text-night/55"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : experts.length === 0 ? (
        <Card className="p-10 text-center rounded-2xl border-dashed border-slate-200" data-testid="directory-empty">
          <p className="text-sm text-night/65">Aucun spécialiste ne correspond à ces critères pour le moment.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
          {experts.map((e) => <ExpertCard key={e.id} expert={e} onOpen={() => setSelected(e.id)} />)}
        </div>
      )}

      {selected && <ProfileDrawer expertId={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function ExpertCard({ expert, onOpen }) {
  // Construit l'étiquette TITRE (en haut, capitales fines) à partir des
  // champs disponibles. Hiérarchie : `title` explicite > `role` > `profession`
  // (en MAJUSCULES). Reste neutre côté juridique : pas de qualification non
  // confirmée par le spécialiste lui-même.
  const titleLabel = (expert.title || expert.role || expert.profession || "Expert")
    .toString()
    .toUpperCase();

  // Juridiction : `TERRITOIRE, VILLE` (CMS pattern) — fallback si l'un manque.
  const territory = (expert.territory || expert.country || "").toString().toUpperCase();
  const territoryLabel = territory === "FR" ? "FRANCE" : territory === "CH" ? "SUISSE" : territory;
  const cityLabel = (expert.city || "").toString().toUpperCase();
  const jurisdiction = [territoryLabel, cityLabel].filter(Boolean).join(", ");

  // Spécialités : 2 affichées + « +N PLUS » si plus nombreuses.
  const specs = expert.specialties || [];
  const primary = specs.slice(0, 2);
  const extra = specs.length - primary.length;

  return (
    <button
      onClick={onOpen}
      data-testid={`expert-card-${expert.id}`}
      className="text-left group p-6 rounded-2xl border border-slate-100 bg-white hover:border-copper/50 hover:shadow-[0_18px_60px_-25px_rgba(196,164,104,0.35)] transition-smooth flex flex-col h-full"
    >
      {/* TITRE en haut (CMS pattern) */}
      <p className="text-[10px] uppercase tracking-[0.24em] text-copper mb-3" data-testid={`expert-title-${expert.id}`}>
        {titleLabel}
      </p>

      {/* Identité — photo + nom + rôle */}
      <div className="flex items-start gap-4 mb-4">
        {expert.photo_url ? (
          <img
            src={expert.photo_url}
            alt={`Portrait ${expert.first_name}`}
            className="w-14 h-14 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-ivory shrink-0 flex items-center justify-center text-night/40 font-serif text-lg">
            {(expert.first_name?.[0] || "?") + (expert.last_name?.[0] || "")}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif italic text-lg text-night leading-tight flex items-center gap-1.5">
            <span className="truncate">{expert.first_name} {expert.last_name}</span>
            {expert.verified && (
              <BadgeCheck className="w-4 h-4 text-emerald-600 shrink-0" aria-label="Vérifié" />
            )}
          </h3>
          {expert.presentation && (
            <p className="text-[12px] text-night/60 mt-1 leading-snug line-clamp-2">
              {expert.presentation}
            </p>
          )}
        </div>
      </div>

      {/* Juridiction */}
      {jurisdiction && (
        <p className="text-[10px] uppercase tracking-[0.22em] text-night/60 mb-3 flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> {jurisdiction}
        </p>
      )}

      {/* Spécialités — 2 + "+N PLUS" */}
      <div className="mt-auto pt-3 border-t border-slate-100 space-y-1.5">
        {primary.map((s) => (
          <p
            key={s}
            className="text-[11px] uppercase tracking-[0.2em] text-night/80 truncate"
            data-testid={`expert-specialty-${expert.id}-${s}`}
          >
            {s}
          </p>
        ))}
        {extra > 0 && (
          <p className="text-[10px] uppercase tracking-[0.22em] text-copper font-medium" data-testid={`expert-extra-${expert.id}`}>
            +{extra} PLUS
          </p>
        )}
      </div>
    </button>
  );
}

// =============================================================================
// Drawer fiche pro
// =============================================================================
function ProfileDrawer({ expertId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [expert, setExpert] = useState(null);
  const [opening, setOpening] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/network/experts/${expertId}`);
        setExpert(data.expert);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Fiche indisponible");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [expertId]);

  const contact = async () => {
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/network/conversations", {
        target_expert_id: expertId,
        opening_message: opening?.trim() || null,
      });
      toast.success("Conversation ouverte. Retrouvez-la dans l'onglet Conversations.");
      window.location.hash = `conv-${data.conversation_id}`;
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer la conversation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="profile-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-white shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <p className="text-[10px] uppercase tracking-[0.2em] text-copper">Fiche professionnelle</p>
          <button onClick={onClose} className="text-night/60 hover:text-night" data-testid="drawer-close"><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-night/40" /></div>
        ) : expert ? (
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-4">
              {expert.photo_url ? (
                <img src={expert.photo_url} alt={`Portrait ${spécialiste.first_name}`} className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-night/40 font-serif text-2xl">
                  {(expert.first_name?.[0] || "?") + (expert.last_name?.[0] || "")}
                </div>
              )}
              <div>
                <h2 className="font-serif text-xl text-night flex items-center gap-2">
                  {expert.first_name} {expert.last_name}
                  {expert.verified && <BadgeCheck className="w-4 h-4 text-emerald-600" />}
                </h2>
                <p className="text-[12px] uppercase tracking-[0.16em] text-copper mt-1">{expert.profession}</p>
                <p className="text-xs text-night/60 mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {expert.city} · {expert.territory || expert.country}</p>
              </div>
            </div>
            {expert.professional_bio && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-night/55 mb-1.5">Présentation</p>
                <p className="text-sm text-night/80 leading-relaxed whitespace-pre-wrap">{expert.professional_bio}</p>
              </div>
            )}
            {expert.specialties?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-night/55 mb-1.5">Spécialités</p>
                <div className="flex flex-wrap gap-1.5">
                  {expert.specialties.map((s) => <Badge key={s} variant="outline" className="text-[11px] font-normal">{s}</Badge>)}
                </div>
              </div>
            )}
            {expert.languages?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-night/55 mb-1.5">Langues</p>
                <p className="text-sm text-night/80">{expert.languages.join(" · ")}</p>
              </div>
            )}
            {expert.certifications?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-night/55 mb-1.5">Certifications</p>
                <ul className="text-sm text-night/80 space-y-1">
                  {expert.certifications.map((c, i) => <li key={i} className="flex items-start gap-2"><BadgeCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> {c}</li>)}
                </ul>
              </div>
            )}

            <div className="rounded-xl bg-ivory border border-night/10 p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-copper mb-2">Contacter cet spécialiste (gratuit)</p>
              <Textarea
                value={opening}
                onChange={(e) => setOpening(e.target.value)}
                placeholder="Message d'ouverture (facultatif)…"
                rows={4}
                className="mb-3"
                data-testid="opening-message"
              />
              <div className="flex flex-wrap gap-2">
                <Button onClick={contact} disabled={submitting} className="rounded-full bg-night text-paper hover:bg-night/90" data-testid="contact-expert-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                  Contacter
                </Button>
              </div>
              <p className="text-[10px] text-night/55 italic mt-3">
                Aucun paiement, aucune commission. Inclus dans votre abonnement CONFILEX.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// =============================================================================
// Conversations
// =============================================================================
function Conversations({ onForbidden }) {
  const [loading, setLoading] = useState(true);
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/network/conversations");
      setConvs(data.conversations || []);
      // Si un hash de conversation est passé via URL, le sélectionner
      const hash = window.location.hash.match(/conv-([0-9a-f-]+)/);
      if (hash && hash[1]) setActiveId(hash[1]);
    } catch (e) {
      if (e?.response?.status === 403) onForbidden(e.response.data?.detail);
      else toast.error("Impossible de charger les conversations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="conversations-panel">
      <Card className="p-3 rounded-2xl border-slate-100 h-fit lg:max-h-[70vh] overflow-y-auto">
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-night/40" /></div>
        ) : convs.length === 0 ? (
          <p className="text-xs text-night/55 text-center py-10">Aucune conversation pour le moment.</p>
        ) : (
          <ul className="space-y-1" data-testid="conversations-list">
            {convs.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => setActiveId(c.id)}
                  data-testid={`conv-item-${c.id}`}
                  className={`w-full text-left rounded-lg p-3 transition-smooth ${activeId === c.id ? "bg-ivory border border-copper/30" : "hover:bg-ivory/60"}`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-sm text-night truncate">{c.other_expert.first_name} {c.other_expert.last_name}</span>
                    {c.other_expert.verified && <BadgeCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                  </div>
                  <p className="text-[11px] text-night/55">{c.other_expert.profession}{c.status !== "accepted" && c.status !== "pending" ? ` · ${c.status}` : ""}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <div>
        {activeId ? <ChatPanel conversationId={activeId} onChanged={refresh} /> : (
          <Card className="p-10 rounded-2xl border-dashed border-slate-200 text-center text-sm text-night/55">
            Sélectionnez une conversation pour la lire.
          </Card>
        )}
      </div>
    </div>
  );
}

function ChatPanel({ conversationId, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("pending");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/network/conversations/${conversationId}/messages`);
      setMessages(data.messages || []);
      setStatus(data.status || "pending");
    } catch {
      toast.error("Impossible de charger les messages");
    } finally {
      setLoading(false);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [conversationId]);

  const send = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await apiClient.post(`/network/conversations/${conversationId}/messages`, { body: body.trim() });
      setBody("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const action = async (path, payload) => {
    try {
      await apiClient.post(`/network/conversations/${conversationId}/${path}`, payload || {});
      toast.success("Action enregistrée");
      onChanged?.();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action impossible");
    }
  };

  const blocked = status === "blocked";
  const refused = status === "refused";

  return (
    <Card className="rounded-2xl border-slate-100 flex flex-col h-[70vh]" data-testid="chat-panel">
      <header className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-copper">Conversation</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full text-[11px]" onClick={() => action("block")} data-testid="block-btn">
            <Ban className="w-3 h-3 mr-1.5" /> Bloquer
          </Button>
          <Button size="sm" variant="outline" className="rounded-full text-[11px]" onClick={() => {
            const reason = window.prompt("Motif du signalement :");
            if (reason && reason.trim().length >= 3) action("report", { reason });
          }} data-testid="report-btn">
            <Flag className="w-3 h-3 mr-1.5" /> Signaler
          </Button>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-night/40" /></div>
        ) : messages.length === 0 ? (
          <p className="text-xs text-night/55 text-center py-10">Aucun message — démarrez la conversation.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`max-w-[80%] ${m.from_me ? "ml-auto" : ""}`}>
              <div className={`rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.from_me ? "bg-night text-paper" : "bg-ivory text-night border border-night/10"}`}>
                {m.body}
              </div>
              <p className="text-[10px] text-night/40 mt-1 px-1">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2">
        <Input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={blocked ? "Conversation bloquée" : refused ? "Conversation refusée" : "Écrire un message…"}
          disabled={blocked || refused || sending}
          data-testid="message-input"
        />
        <Button type="submit" disabled={blocked || refused || sending || !body.trim()} className="bg-night text-paper hover:bg-night/90" data-testid="message-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </Card>
  );
}
